import * as signalR from '@microsoft/signalr';
import { API_URL } from '../config/api';
import { getAuthToken } from '../config/auth';

export const KNOWLEDGE_QUERY_KEY = ['knowledge-tree'];

export interface KnowledgeDocumentCreatedEvent {
  id: string; // ID документа
  name: string; // Название документа
  parentId?: string; // ID родительской папки
  fileType: string; // Тип файла (article, content-plan, kanban и т.д.)
  itemType: string; // Тип элемента (file, folder)
  content?: string; // Содержимое документа
  userId: string; // ID создателя
  createdAt: string; // Время создания
}

export interface KnowledgeDocumentUpdatedEvent {
  id: string; // ID документа
  name?: string;
  content?: string;
  updatedAt: string;
  
  // Информация о редакторе
  editorUserId?: string;
  editorUsername?: string;
  
  // Список участников для уведомления
  participantUserIds?: string[];
}

export interface KnowledgeDocumentDeletedEvent {
  id: string; // ID документа
  parentId?: string; // ID родительской папки
}

export interface KnowledgeDocumentAccessRevokedEvent {
  id: string; // ID документа
  name?: string; // Название документа
  parentId?: string; // ID родительской папки
  revokedUserId: string; // ID исключенного пользователя
  revokedByUserId: string; // ID пользователя, который исключил
  revokedByUsername?: string; // Имя пользователя, который исключил
  revokedAt: string; // Время исключения
}

export interface KnowledgeTreeChangedEvent {
  userId: string;
  changeType: string; // "move", "rename", "structure"
  changedAt: string;
}


class KnowledgeSignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000;

  // Обработчики событий
  private onDocumentCreated: ((event: KnowledgeDocumentCreatedEvent) => void)[] = [];
  private onDocumentUpdated: ((event: KnowledgeDocumentUpdatedEvent) => void)[] = [];
  private onDocumentDeleted: ((event: KnowledgeDocumentDeletedEvent) => void)[] = [];
  private onDocumentAccessRevoked: ((event: KnowledgeDocumentAccessRevokedEvent) => void)[] = [];
  private onTreeChanged: ((event: KnowledgeTreeChangedEvent) => void)[] = [];
  private onConnectionStatusChange: ((connected: boolean) => void)[] = [];


  constructor() {
    this.initializeConnection();
  }

  private initializeConnection() {
    const apiUrl = API_URL;
    // Убираем /api из URL, если он есть, для совместимости с конфигурацией сервера
    const hubUrl = `${apiUrl.replace(/\/api$/, '')}/knowledgeHub`;

    console.log('HUB', hubUrl)

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => getAuthToken() || ''
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
            const delay = this.reconnectInterval * (retryContext.previousRetryCount + 1);
            console.log(`[KnowledgeSignalR] Попытка переподключения ${retryContext.previousRetryCount + 1}/${this.maxReconnectAttempts} через ${delay / 1000}с`);
            return delay;
          }
          console.log('[KnowledgeSignalR] Достигнуто максимальное количество попыток переподключения.');
          return null; // Прекратить попытки
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupEventHandlers();
  }


  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Исправляем имена событий на PascalCase в соответствии с бэкендом
    this.connection.on('KnowledgeDocumentCreated', (event: KnowledgeDocumentCreatedEvent) => {
      console.log('✅ [SignalR] Получено событие: DocumentCreated', event);
      this.onDocumentCreated.forEach(handler => handler(event));
    });

    this.connection.on('KnowledgeDocumentUpdated', (event: KnowledgeDocumentUpdatedEvent) => {
      console.log('✅ [SignalR] Получено событие: DocumentUpdated', event);
      this.onDocumentUpdated.forEach(handler => handler(event));
    });

    this.connection.on('KnowledgeDocumentDeleted', (event: KnowledgeDocumentDeletedEvent) => {
      console.log('✅ [SignalR] Получено событие: DocumentDeleted', event);
      this.onDocumentDeleted.forEach(handler => handler(event));
    });

    this.connection.on('KnowledgeDocumentAccessRevoked', (event: KnowledgeDocumentAccessRevokedEvent) => {
      console.log('✅ [SignalR] Получено событие: DocumentAccessRevoked', event);
      this.onDocumentAccessRevoked.forEach(handler => handler(event));
    });

    this.connection.on('KnowledgeTreeChanged', (event: KnowledgeTreeChangedEvent) => {
      console.log('✅ [SignalR] Получено событие: TreeChanged', event);
      this.onTreeChanged.forEach(handler => handler(event));
    });
    
    this.connection.onreconnecting(error => {
        console.warn(`[KnowledgeSignalR] Соединение потеряно, идет переподключение...`, error);
        this.notifyConnectionStatus(false);
    });

    this.connection.onreconnected(connectionId => {
        console.log(`[KnowledgeSignalR] Соединение восстановлено. ID: ${connectionId}`);
        this.notifyConnectionStatus(true);
    });

    this.connection.onclose(error => {
        console.error(`[KnowledgeSignalR] Соединение закрыто.`, error);
        this.isConnecting = false;
        this.notifyConnectionStatus(false);
    });
  }

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      console.log('[KnowledgeSignalR] 🚀 Инициируем подключение...');
      await this.connection?.start();
      console.log('[KnowledgeSignalR] ✅ Подключение успешно установлено.');
      this.notifyConnectionStatus(true);
    } catch (err) {
      console.error('[KnowledgeSignalR] ❌ Ошибка подключения:', err);
      this.notifyConnectionStatus(false);
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.stop();
      console.log('[KnowledgeSignalR] 🛑 Соединение остановлено.');
      this.notifyConnectionStatus(false);
    }
  }

  // Методы подписки/отписки
  subscribeToDocumentCreated(handler: (event: KnowledgeDocumentCreatedEvent) => void) {
    this.onDocumentCreated.push(handler);
  }

  unsubscribeFromDocumentCreated(handler: (event: KnowledgeDocumentCreatedEvent) => void) {
    this.onDocumentCreated = this.onDocumentCreated.filter(h => h !== handler);
  }

  subscribeToDocumentUpdated(handler: (event: KnowledgeDocumentUpdatedEvent) => void) {
    this.onDocumentUpdated.push(handler);
  }

  unsubscribeFromDocumentUpdated(handler: (event: KnowledgeDocumentUpdatedEvent) => void) {
    this.onDocumentUpdated = this.onDocumentUpdated.filter(h => h !== handler);
  }

  subscribeToDocumentDeleted(handler: (event: KnowledgeDocumentDeletedEvent) => void) {
    this.onDocumentDeleted.push(handler);
  }

  unsubscribeFromDocumentDeleted(handler: (event: KnowledgeDocumentDeletedEvent) => void) {
    this.onDocumentDeleted = this.onDocumentDeleted.filter(h => h !== handler);
  }

  subscribeToDocumentAccessRevoked(handler: (event: KnowledgeDocumentAccessRevokedEvent) => void) {
    this.onDocumentAccessRevoked.push(handler);
  }

  unsubscribeFromDocumentAccessRevoked(handler: (event: KnowledgeDocumentAccessRevokedEvent) => void) {
    this.onDocumentAccessRevoked = this.onDocumentAccessRevoked.filter(h => h !== handler);
  }
  
  subscribeToTreeChanged(handler: (event: KnowledgeTreeChangedEvent) => void) {
    this.onTreeChanged.push(handler);
  }

  unsubscribeFromTreeChanged(handler: (event: KnowledgeTreeChangedEvent) => void) {
    this.onTreeChanged = this.onTreeChanged.filter(h => h !== handler);
  }
  
  subscribeToConnectionStatus(handler: (connected: boolean) => void) {
    this.onConnectionStatusChange.push(handler);
  }

  unsubscribeFromConnectionStatus(handler: (connected: boolean) => void) {
    this.onConnectionStatusChange = this.onConnectionStatusChange.filter(h => h !== handler);
  }
  
  private notifyConnectionStatus(connected: boolean) {
      this.onConnectionStatusChange.forEach(handler => handler(connected));
  }

  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Экспортируем синглтон
export const knowledgeSignalRService = new KnowledgeSignalRService(); 

